import React, { useMemo, useRef, useState, useEffect } from 'react';

import { timezoneGroups, tzLabel } from '../../config/timezones';

/**
 * Searchable, grouped timezone picker for the admin panel.
 *
 * WHY IT'S HAND-ROLLED
 * The admin app has no dropdown/combobox dependency (plain React +
 * react-hook-form + Tailwind), so this is a small combobox rather than a
 * library widget: filter input + grouped list + keyboard nav.
 *
 * WHY IT REPLACED A <select>
 * The previous field was a native <select> listing 25 curated zones, wired with
 * register() — i.e. uncontrolled, with react-hook-form reading the value
 * straight off the DOM at submit time. A <select> given a value it has no
 * <option> for reports "" and selectedIndex -1. So opening a company whose
 * timezone wasn't among those 25 (Asia/Tehran, Africa/Lagos, anything signup
 * captured from a browser) showed a blank field, and saving ANY unrelated
 * field — status, device limit — silently wrote that blank back and wiped the
 * company's timezone. Silent data loss, no warning, no interaction required.
 *
 * Two things fix it here: the list now covers every zone the backend accepts
 * (and folds in an unrecognised stored value as its own group), and the value
 * is held in React state rather than read off the DOM, so an unrepresentable
 * value can never degrade to "".
 *
 * Controlled: pass value/onChange (wire through RHF's <Controller>, never
 * register()).
 */
export default function TimezoneSelect({ value, onChange, label, allowUnset = true, error }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const boxRef = useRef(null);

    const groups = useMemo(() => timezoneGroups(value), [value]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return groups;
        return groups
            .map((g) => ({ ...g, items: g.items.filter((i) => i.label.toLowerCase().includes(q)) }))
            .filter((g) => g.items.length);
    }, [groups, query]);

    // Close on outside click.
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const pick = (zone) => { onChange(zone); setOpen(false); setQuery(''); };

    const display = value ? tzLabel(value) : '— not set —';

    return (
        <div className="w-full" ref={boxRef}>
            {label && <label className="label">{label}</label>}

            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={`input-base w-full text-left flex items-center justify-between ${error ? 'border-danger/50' : ''}`}
            >
                <span className={value ? '' : 'text-text-muted'}>{display}</span>
                <span className="text-text-muted text-xs ml-2">▾</span>
            </button>

            {open && (
                <div className="relative">
                    <div className="absolute z-50 mt-1 w-full rounded-xl border border-white/[0.08] bg-[#12151c] shadow-2xl max-h-72 overflow-auto">
                        <div className="p-2 sticky top-0 bg-[#12151c] border-b border-white/[0.06]">
                            <input
                                autoFocus
                                className="input-base w-full text-sm"
                                placeholder="Search a timezone…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                        </div>

                        {allowUnset && !query && (
                            <button type="button" onClick={() => pick(null)}
                                className="w-full text-left px-3 py-2 text-sm text-text-muted hover:bg-white/[0.05]">
                                — not set (uses the Asia/Riyadh fallback) —
                            </button>
                        )}

                        {filtered.length === 0 && (
                            <p className="px-3 py-3 text-sm text-text-muted">No matching timezone</p>
                        )}

                        {filtered.map((g) => (
                            <div key={g.label || 'ungrouped'}>
                                {g.label && (
                                    <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-text-muted">{g.label}</p>
                                )}
                                {g.items.map((i) => (
                                    <button
                                        key={i.value}
                                        type="button"
                                        onClick={() => pick(i.value)}
                                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-white/[0.05] ${
                                            i.value === value ? 'text-teal font-semibold' : 'text-text-main'
                                        }`}
                                    >
                                        {i.label}
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {error && <p className="mt-1 text-xs text-danger">{error}</p>}
        </div>
    );
}
